import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  Building2, Save, CheckCircle, Loader, Users, TrendingUp,
  Target, ShoppingBag, BarChart3, ChevronRight, ChevronLeft,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/compass/company`;

const STEPS = ['basis', 'financieel', 'team', 'producten', 'markt', 'doelen'];

const CompanyProfile = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [completeness, setCompleteness] = useState(0);
  const [profile, setProfile] = useState({
    company_name: '', sector: '', sub_sector: '', founded_year: null,
    location: '', num_employees: null, company_type: '',
    annual_revenue: null, monthly_costs: null, profit_margin: null,
    seasonal_patterns: '', growth_rate: null,
    departments: '', key_roles: '', hiring_plans: '',
    products_services: '', unique_selling_points: '', pricing_model: '',
    target_audience: '', main_competitors: '', market_position: '',
    customer_count: null,
    short_term_goals: '', long_term_goals: '', main_challenges: '',
    additional_notes: '',
  });

  const headers = { Authorization: `Bearer ${token}` };

  const txt = lang === 'nl' ? {
    title: 'Bedrijfsprofiel',
    subtitle: 'Je Digital Twin - de basis voor alle AI-analyses',
    completeness: 'Compleetheid',
    save: 'Opslaan',
    saved: 'Opgeslagen!',
    next: 'Volgende',
    prev: 'Vorige',
    finish: 'Profiel Voltooien',
    steps: {
      basis: { title: 'Basisgegevens', icon: Building2 },
      financieel: { title: 'Financieel', icon: BarChart3 },
      team: { title: 'Team', icon: Users },
      producten: { title: 'Producten & Diensten', icon: ShoppingBag },
      markt: { title: 'Klanten & Markt', icon: Target },
      doelen: { title: 'Doelen & Uitdagingen', icon: TrendingUp },
    },
    fields: {
      company_name: 'Bedrijfsnaam', sector: 'Sector', sub_sector: 'Sub-sector',
      founded_year: 'Opgericht in', location: 'Locatie', num_employees: 'Aantal medewerkers',
      company_type: 'Rechtsvorm',
      annual_revenue: 'Jaaromzet (EUR)', monthly_costs: 'Maandkosten (EUR)',
      profit_margin: 'Winstmarge (%)', seasonal_patterns: 'Seizoenspatronen',
      growth_rate: 'Groeipercentage (%)',
      departments: 'Afdelingen', key_roles: 'Belangrijke functies', hiring_plans: 'Wervingsplannen',
      products_services: 'Producten & Diensten', unique_selling_points: 'Unieke verkooppunten',
      pricing_model: 'Prijsmodel',
      target_audience: 'Doelgroep', main_competitors: 'Belangrijkste concurrenten',
      market_position: 'Marktpositie', customer_count: 'Aantal klanten',
      short_term_goals: 'Korte termijn doelen (1 jaar)', long_term_goals: 'Lange termijn doelen (3-5 jaar)',
      main_challenges: 'Belangrijkste uitdagingen', additional_notes: 'Extra informatie',
    },
    placeholders: {
      company_name: 'Bijv: TechBV', sector: 'Bijv: IT, Retail, Horeca, Bouw...',
      location: 'Bijv: Amsterdam', company_type: 'Bijv: BV, VOF, ZZP, NV...',
      seasonal_patterns: 'Bijv: Piek in december, rustig in januari-februari',
      products_services: 'Beschrijf je producten en/of diensten...',
      unique_selling_points: 'Wat maakt jullie uniek?',
      target_audience: 'Wie zijn je ideale klanten?',
      main_competitors: 'Noem je belangrijkste concurrenten...',
      short_term_goals: 'Wat wil je bereiken dit jaar?',
      long_term_goals: 'Waar wil je over 3-5 jaar staan?',
      main_challenges: 'Wat zijn de grootste obstakels?',
    },
  } : {
    title: 'Company Profile',
    subtitle: 'Your Digital Twin - the foundation for all AI analyses',
    completeness: 'Completeness',
    save: 'Save', saved: 'Saved!', next: 'Next', prev: 'Previous', finish: 'Complete Profile',
    steps: {
      basis: { title: 'Basics', icon: Building2 },
      financieel: { title: 'Financial', icon: BarChart3 },
      team: { title: 'Team', icon: Users },
      producten: { title: 'Products & Services', icon: ShoppingBag },
      markt: { title: 'Clients & Market', icon: Target },
      doelen: { title: 'Goals & Challenges', icon: TrendingUp },
    },
    fields: {
      company_name: 'Company Name', sector: 'Sector', sub_sector: 'Sub-sector',
      founded_year: 'Founded in', location: 'Location', num_employees: 'Number of employees',
      company_type: 'Legal form', annual_revenue: 'Annual revenue (EUR)',
      monthly_costs: 'Monthly costs (EUR)', profit_margin: 'Profit margin (%)',
      seasonal_patterns: 'Seasonal patterns', growth_rate: 'Growth rate (%)',
      departments: 'Departments', key_roles: 'Key roles', hiring_plans: 'Hiring plans',
      products_services: 'Products & Services', unique_selling_points: 'Unique selling points',
      pricing_model: 'Pricing model', target_audience: 'Target audience',
      main_competitors: 'Main competitors', market_position: 'Market position',
      customer_count: 'Number of customers', short_term_goals: 'Short term goals (1 year)',
      long_term_goals: 'Long term goals (3-5 years)', main_challenges: 'Main challenges',
      additional_notes: 'Additional information',
    },
    placeholders: {
      company_name: 'E.g.: TechBV', sector: 'E.g.: IT, Retail, Hospitality...',
      location: 'E.g.: Amsterdam', company_type: 'E.g.: BV, LLC, Sole proprietor...',
      seasonal_patterns: 'E.g.: Peak in December, quiet in January-February',
      products_services: 'Describe your products and/or services...',
      unique_selling_points: 'What makes you unique?',
      target_audience: 'Who are your ideal customers?',
      main_competitors: 'Name your main competitors...',
      short_term_goals: 'What do you want to achieve this year?',
      long_term_goals: 'Where do you want to be in 3-5 years?',
      main_challenges: 'What are the biggest obstacles?',
    },
  };

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`, { headers });
      if (res.data.profile) {
        const p = res.data.profile;
        // Normalize: convert any arrays to comma-separated strings
        for (const key of Object.keys(p)) {
          if (Array.isArray(p[key])) p[key] = p[key].join(', ');
        }
        setProfile(prev => ({ ...prev, ...p }));
      }
      setCompleteness(res.data.completeness || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      // Sanitize before sending: convert arrays to strings, empty strings to null for numbers
      const numFields = ['founded_year', 'num_employees', 'annual_revenue', 'monthly_costs', 'profit_margin', 'growth_rate', 'customer_count'];
      const cleaned = {};
      for (const [key, val] of Object.entries(profile)) {
        if ((Array.isArray(numFields) ? numFields : []).includes(key)) {
          cleaned[key] = (val === '' || val === null || val === undefined) ? null : Number(val);
        } else if (Array.isArray(val)) {
          cleaned[key] = (Array.isArray(val) ? val : []).join(', ');
        } else {
          cleaned[key] = val;
        }
      }
      const res = await axios.post(`${API}/profile`, cleaned, { headers });
      setCompleteness(res.data.completeness || 0);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      let msg = 'Error saving';
      if (typeof errDetail === 'string') msg = errDetail;
      else if (Array.isArray(errDetail)) msg = (Array.isArray(errDetail) ? errDetail : []).map(e => e.msg || e.message || String(e)).join(', ');
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const renderInput = (field, type = 'text', multiline = false) => {
    const val = profile[field] ?? '';
    const ph = txt.placeholders[field] || '';
    if (multiline) {
      return (
        <div className="form-group" key={field}>
          <label className="body-small" style={{ fontWeight: 600 }}>{txt.fields[field]}</label>
          <textarea className="form-input auto-textarea" rows={3} placeholder={ph}
            value={val} onChange={(e) => updateField(field, e.target.value)} data-testid={`field-${field}`} />
        </div>
      );
    }
    return (
      <div className="form-group" key={field}>
        <label className="body-small" style={{ fontWeight: 600 }}>{txt.fields[field]}</label>
        <input type={type} className="form-input" placeholder={ph}
          value={val || ''} onChange={(e) => updateField(field, type === 'number' ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
          data-testid={`field-${field}`} />
      </div>
    );
  };

  const stepFields = {
    basis: () => (<>
      {renderInput('company_name')}
      {renderInput('sector')}
      {renderInput('sub_sector')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {renderInput('founded_year', 'number')}
        {renderInput('location')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {renderInput('num_employees', 'number')}
        {renderInput('company_type')}
      </div>
    </>),
    financieel: () => (<>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {renderInput('annual_revenue', 'number')}
        {renderInput('monthly_costs', 'number')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {renderInput('profit_margin', 'number')}
        {renderInput('growth_rate', 'number')}
      </div>
      {renderInput('seasonal_patterns', 'text', true)}
    </>),
    team: () => (<>
      {renderInput('departments', 'text', true)}
      {renderInput('key_roles', 'text', true)}
      {renderInput('hiring_plans', 'text', true)}
    </>),
    producten: () => (<>
      {renderInput('products_services', 'text', true)}
      {renderInput('unique_selling_points', 'text', true)}
      {renderInput('pricing_model')}
    </>),
    markt: () => (<>
      {renderInput('target_audience', 'text', true)}
      {renderInput('main_competitors', 'text', true)}
      {renderInput('market_position')}
      {renderInput('customer_count', 'number')}
    </>),
    doelen: () => (<>
      {renderInput('short_term_goals', 'text', true)}
      {renderInput('long_term_goals', 'text', true)}
      {renderInput('main_challenges', 'text', true)}
      {renderInput('additional_notes', 'text', true)}
    </>),
  };

  if (loading) return <div className="tool-page"><div className="tool-content" style={{ textAlign: 'center', padding: '3rem' }}><Loader className="spin" size={24} /></div></div>;

  const currentStep = STEPS[step];
  const StepIcon = txt.steps[currentStep]?.icon || Building2;

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1">
          <Building2 size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
          {txt.title}
        </h1>
      </div>
      <div className="tool-content" style={{ maxWidth: '800px' }}>
        <p className="body-medium text-secondary" style={{ marginBottom: '1.5rem' }}>{txt.subtitle}</p>

        {/* Progress bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }} data-testid="completeness-bar">
          <span className="body-small" style={{ fontWeight: 600 }}>{txt.completeness}: {completeness}%</span>
          <div style={{ flex: 1, height: 6, background: 'var(--border-light)', borderRadius: 3 }}>
            <div style={{ width: `${completeness}%`, height: '100%', background: completeness >= 70 ? 'var(--accent-green)' : completeness >= 40 ? 'var(--accent-orange)' : 'var(--accent-blue)', borderRadius: 3, transition: '0.3s' }} />
          </div>
        </div>

        {/* Step navigation */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {(Array.isArray(STEPS) ? STEPS : []).map((s, i) => {
            const SIcon = txt.steps[s]?.icon || Building2;
            return (
              <button key={s} onClick={() => setStep(i)}
                className={`compass-nav-item ${i === step ? 'active' : ''}`}
                style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }} data-testid={`step-${s}`}>
                <SIcon size={14} /> {txt.steps[s]?.title}
              </button>
            );
          })}
        </div>

        {/* Current step form */}
        <div className="auto-input-card" data-testid="profile-form">
          <div className="auto-input-header">
            <StepIcon size={20} />
            <h3 className="heading-3">{txt.steps[currentStep]?.title}</h3>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {stepFields[currentStep]?.()}
          </div>
        </div>

        {/* Navigation + Save */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
          {step > 0 && (
            <button className="btn-secondary" onClick={() => setStep(step - 1)}>
              <ChevronLeft size={16} /> {txt.prev}
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <>
              <button className="btn-primary" onClick={saveProfile} disabled={saving} data-testid="save-btn">
                {saving ? <Loader className="spin" size={16} /> : saved ? <CheckCircle size={16} /> : <Save size={16} />}
                {saved ? txt.saved : txt.save}
              </button>
              <button className="btn-secondary" onClick={() => setStep(step + 1)} data-testid="next-btn">
                {txt.next} <ChevronRight size={16} />
              </button>
            </>
          ) : (
            <button className="btn-primary" data-testid="finish-btn" disabled={saving} onClick={async () => {
              await saveProfile();
              navigate('/portal', { state: { profileCompleted: true } });
            }} style={{ background: '#16a34a', borderColor: '#16a34a', color: '#fff' }}>
              {saving ? <Loader className="spin" size={16} /> : <CheckCircle size={16} />}
              {txt.finish}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
